// @ts-nocheck
import { useEffect, useMemo, useRef, useState } from "react";
import { type WalletLedger, type SimResult } from "@/lib/algo/choreography";
import {
  type FlowGraph,
  type GraphEdge,
  type SellNode,
} from "@/lib/algo/graph";

// ─── Types ───────────────────────────────────────────────────────────────────

type BubbleRole = "bundle" | "spine" | "rotate-in" | "rotate-out" | "seller" | "dev" | "disabled";

type BubbleData = {
  id: string;
  label: string;
  sol: number;
  role: BubbleRole;
  ledger?: WalletLedger;
  tokenPct: number;       // % of total tokens this wallet holds
  enabled: boolean;
};

// ─── Constants ────────────────────────────────────────────────────────────────

const ROLE_COLOR: Record<BubbleRole, { fill: string; stroke: string; glow: string; text: string }> = {
  bundle:      { fill: "#065f46", stroke: "#10b981", glow: "#10b98155", text: "#6ee7b7" },
  spine:       { fill: "#1e3a5f", stroke: "#3b82f6", glow: "#3b82f655", text: "#93c5fd" },
  "rotate-in": { fill: "#2d1b69", stroke: "#8b5cf6", glow: "#8b5cf655", text: "#c4b5fd" },
  "rotate-out":{ fill: "#3b1f5e", stroke: "#a855f7", glow: "#a855f755", text: "#d8b4fe" },
  seller:      { fill: "#4c1414", stroke: "#ef4444", glow: "#ef444455", text: "#fca5a5" },
  dev:         { fill: "#713f12", stroke: "#f59e0b", glow: "#f59e0b66", text: "#fcd34d" },
  disabled:    { fill: "#1a1a1a", stroke: "#404040", glow: "transparent", text: "#555" },
};

const ROLE_LABEL: Record<BubbleRole, string> = {
  bundle:       "Bundle",
  spine:        "Buyer",
  "rotate-in":  "Rotated In",
  "rotate-out": "Rotator",
  seller:       "Seller",
  dev:          "Dev",
  disabled:     "Disabled",
};

// ─── Helpers ─────────────────────────────────────────────────────────────────

function compact(n: number): string {
  if (n >= 1_000_000) return `${(n / 1_000_000).toFixed(1)}M`;
  if (n >= 1_000) return `${(n / 1_000).toFixed(1)}K`;
  return n.toFixed(0);
}

function deriveRole(
  walletId: string,
  edges: GraphEdge[],
  sells: SellNode[],
  bundleIds: Set<string>,
): BubbleRole {
  const incomingBuy = edges.find((e) => e.kind === "buy" && e.target === walletId);
  const isBundle = incomingBuy?.bundleId !== undefined && bundleIds.has(incomingBuy.bundleId);
  if (isBundle) return "bundle";

  const isRotateTarget = edges.some((e) => e.kind === "rotate" && e.target === walletId);
  if (isRotateTarget) return "rotate-in";

  const isRotateSource = edges.some((e) => e.kind === "rotate" && e.source === walletId);
  if (isRotateSource) return "rotate-out";

  const hasSell = sells.some(
    (s) => s.walletId === walletId && (s.trigger === "now" || s.trigger === "time"),
  );
  if (hasSell) return "seller";

  const hasBuy = edges.some((e) => e.kind === "buy" && e.target === walletId);
  if (hasBuy) return "spine";

  return "spine"; // fallback
}

// Very simple repulsion-only layout (no physics loop needed — one-shot settle)
function packBubbles(
  items: { r: number }[],
  width: number,
  height: number,
  iterations = 80,
): { x: number; y: number }[] {
  const cx = width / 2;
  const cy = height / 2;
  // init positions in a spiral
  const pos = items.map((_, i) => {
    const angle = (i / items.length) * Math.PI * 2 * 3;
    const rad = 30 + i * 8;
    return { x: cx + Math.cos(angle) * rad, y: cy + Math.sin(angle) * rad };
  });

  const minX = 40, maxX = width - 40, minY = 40, maxY = height - 40;

  for (let iter = 0; iter < iterations; iter++) {
    const k = 1 - iter / iterations; // cooling
    for (let i = 0; i < pos.length; i++) {
      let dx = 0, dy = 0;
      for (let j = 0; j < pos.length; j++) {
        if (i === j) continue;
        const ddx = pos[i].x - pos[j].x;
        const ddy = pos[i].y - pos[j].y;
        const dist = Math.sqrt(ddx * ddx + ddy * ddy) || 0.1;
        const minDist = items[i].r + items[j].r + 6;
        if (dist < minDist) {
          const push = ((minDist - dist) / dist) * 0.5 * k;
          dx += ddx * push;
          dy += ddy * push;
        }
      }
      // gentle pull toward center
      dx += (cx - pos[i].x) * 0.01 * k;
      dy += (cy - pos[i].y) * 0.01 * k;
      pos[i].x = Math.max(minX + items[i].r, Math.min(maxX - items[i].r, pos[i].x + dx));
      pos[i].y = Math.max(minY + items[i].r, Math.min(maxY - items[i].r, pos[i].y + dy));
    }
  }
  return pos;
}

// ─── Tooltip ─────────────────────────────────────────────────────────────────

function Tooltip({ bubble, x, y, containerW }: {
  bubble: BubbleData & { x: number; y: number; r: number };
  x: number; y: number; containerW: number;
}) {
  const l = bubble.ledger;
  const flipRight = x > containerW * 0.6;
  return (
    <div
      className="pointer-events-none absolute z-50 w-52 rounded-xl border border-white/10 bg-black/90 p-3 shadow-2xl backdrop-blur-md text-xs"
      style={{
        left: flipRight ? x - 220 : x + 14,
        top: Math.max(8, y - 60),
      }}
    >
      <div className="mb-1.5 font-bold text-white text-sm">{bubble.label}</div>
      <div className="mb-2 flex items-center gap-1.5">
        <span
          className="rounded px-1.5 py-0.5 text-[10px] font-bold uppercase tracking-wider"
          style={{ background: ROLE_COLOR[bubble.role].stroke + "22", color: ROLE_COLOR[bubble.role].text }}
        >
          {ROLE_LABEL[bubble.role]}
        </span>
        {!bubble.enabled && (
          <span className="rounded px-1.5 py-0.5 text-[10px] font-bold uppercase tracking-wider bg-white/5 text-white/30">
            Disabled
          </span>
        )}
      </div>
      <div className="space-y-1 text-white/70">
        <Row label="Buy size" val={`${bubble.sol.toFixed(3)} SOL`} />
        <Row label="Token share" val={`${bubble.tokenPct.toFixed(1)}%`} highlight />
        {l && (
          <>
            <Row label="Tokens held" val={compact(l.tokens)} />
            <Row label="Cash left" val={`${l.cashSol.toFixed(3)} SOL`} />
            <Row label="Cost basis" val={`${l.costBasisSol.toFixed(3)} SOL`} />
            <Row label="Realized" val={`${l.realizedSol.toFixed(3)} SOL`} />
            <Row label="Buys / Sells" val={`${l.buys} / ${l.sells}`} />
          </>
        )}
      </div>
    </div>
  );
}

function Row({ label, val, highlight }: { label: string; val: string; highlight?: boolean }) {
  return (
    <div className="flex justify-between gap-2">
      <span className="text-white/40">{label}</span>
      <span className={highlight ? "font-bold text-white" : "text-white/80"}>{val}</span>
    </div>
  );
}

// ─── Legend ───────────────────────────────────────────────────────────────────

function Legend() {
  const roles: BubbleRole[] = ["bundle", "spine", "rotate-in", "rotate-out", "seller", "dev", "disabled"];
  return (
    <div className="flex flex-wrap gap-x-4 gap-y-1.5 px-4 py-2 border-t border-white/5 text-[10px]">
      {roles.map((role) => (
        <div key={role} className="flex items-center gap-1.5">
          <div
            className="size-2.5 rounded-full ring-1"
            style={{ background: ROLE_COLOR[role].fill, ringColor: ROLE_COLOR[role].stroke }}
          />
          <span className="text-white/50">{ROLE_LABEL[role]}</span>
        </div>
      ))}
    </div>
  );
}

// ─── Main Component ──────────────────────────────────────────────────────────

export type BubbleMapPanelProps = {
  graph: FlowGraph;
  ledgers: Map<string, WalletLedger>;
  sim: SimResult;
};

export function BubbleMapPanel({ graph, ledgers, sim }: BubbleMapPanelProps) {
  const containerRef = useRef<HTMLDivElement>(null);
  const [dims, setDims] = useState({ w: 600, h: 400 });
  const [hoveredId, setHoveredId] = useState<string | null>(null);
  const [mousePos, setMousePos] = useState({ x: 0, y: 0 });
  const [mounted, setMounted] = useState(false);

  // Observe container size
  useEffect(() => {
    if (!containerRef.current) return;
    const ro = new ResizeObserver((entries) => {
      const { width, height } = entries[0].contentRect;
      if (width > 0 && height > 0) setDims({ w: width, h: height });
    });
    ro.observe(containerRef.current);
    return () => ro.disconnect();
  }, []);

  useEffect(() => { const t = setTimeout(() => setMounted(true), 50); return () => clearTimeout(t); }, []);

  // Derive bubble set from graph + ledgers
  const bubbles: BubbleData[] = useMemo(() => {
    const bundleIds = new Set(
      graph.edges.filter((e) => e.bundleId).map((e) => e.bundleId!),
    );

    // Total tokens across all wallets (from sim ledgers)
    const totalTokens = sim.ledgers.reduce((s, l) => s + l.tokens, 0) || 1;

    const result: BubbleData[] = [];

    // Dev bubble
    if (graph.devBuySol > 0) {
      const devLedger = ledgers.get("creator");
      const devTokens = devLedger?.tokens ?? 0;
      result.push({
        id: "creator",
        label: "Dev",
        sol: graph.devBuySol,
        role: "dev",
        ledger: devLedger,
        tokenPct: (devTokens / totalTokens) * 100,
        enabled: true,
      });
    }

    // Wallet bubbles
    for (const w of graph.wallets) {
      const buyEdge = graph.edges.find((e) => e.kind === "buy" && e.target === w.id);
      const sol = buyEdge?.sol ?? 0;
      const role: BubbleRole = w.enabled
        ? deriveRole(w.id, graph.edges, graph.sells, bundleIds)
        : "disabled";
      const l = ledgers.get(w.id);
      const tokens = l?.tokens ?? 0;
      result.push({
        id: w.id,
        label: w.label,
        sol,
        role,
        ledger: l,
        tokenPct: (tokens / totalTokens) * 100,
        enabled: w.enabled,
      });
    }

    return result;
  }, [graph, ledgers, sim]);

  // Compute bubble sizes
  const maxSol = Math.max(...bubbles.map((b) => b.sol), 0.01);
  const MIN_R = 18, MAX_R = 64;
  const sized = useMemo(
    () =>
      bubbles.map((b) => ({
        ...b,
        r: MIN_R + (Math.sqrt(b.sol / maxSol)) * (MAX_R - MIN_R),
      })),
    [bubbles, maxSol],
  );

  // Pack bubbles
  const positions = useMemo(
    () => packBubbles(sized.map((b) => ({ r: b.r })), dims.w, dims.h),
    [sized, dims],
  );

  // Merge positions
  const placed = sized.map((b, i) => ({ ...b, x: positions[i].x, y: positions[i].y }));

  // Rotate edges for drawing connection lines
  const rotateLines = useMemo(() => {
    return graph.edges
      .filter((e) => e.kind === "rotate")
      .map((e) => {
        const srcIdx = placed.findIndex((b) => b.id === e.source);
        const tgtIdx = placed.findIndex((b) => b.id === e.target);
        if (srcIdx === -1 || tgtIdx === -1) return null;
        return { src: placed[srcIdx], tgt: placed[tgtIdx] };
      })
      .filter(Boolean) as { src: typeof placed[0]; tgt: typeof placed[0] }[];
  }, [placed, graph.edges]);

  const hoveredBubble = placed.find((b) => b.id === hoveredId) ?? null;

  // Sort stats for side panel
  const topHolders = [...placed]
    .filter((b) => b.enabled && b.tokenPct > 0)
    .sort((a, b) => b.tokenPct - a.tokenPct)
    .slice(0, 10);

  return (
    <div className="flex flex-col h-full min-h-0 bg-[var(--color-bg-editor,#0d0d0f)] select-none relative overflow-hidden">

      {/* Header strip */}
      <div className="flex items-center justify-between px-4 py-2 shrink-0 border-b border-white/5">
        <div className="flex items-center gap-2">
          <div className="size-2 rounded-full bg-violet-500 animate-pulse" />
          <span className="text-xs font-bold text-white/70 uppercase tracking-widest">Bubble Map</span>
          <span className="text-[10px] text-white/30 font-medium">Token concentration · live preview</span>
        </div>
        <div className="flex items-center gap-4 text-[10px] text-white/40">
          <span>{placed.filter((b) => b.enabled).length} active wallets</span>
          <span>·</span>
          <span>bubble size = SOL deployed</span>
        </div>
      </div>

      {/* Main area */}
      <div className="flex-1 min-h-0 flex">
        {/* SVG canvas */}
        <div
          ref={containerRef}
          className="flex-1 relative"
          onMouseMove={(e) => {
            const rect = e.currentTarget.getBoundingClientRect();
            setMousePos({ x: e.clientX - rect.left, y: e.clientY - rect.top });
          }}
          onMouseLeave={() => setHoveredId(null)}
        >
          <svg
            width={dims.w}
            height={dims.h}
            className="absolute inset-0"
            style={{ transition: "all 0.3s" }}
          >
            <defs>
              {placed.map((b) => (
                <radialGradient key={`grad-${b.id}`} id={`grad-${b.id}`} cx="35%" cy="35%" r="65%">
                  <stop offset="0%" stopColor={ROLE_COLOR[b.role].stroke} stopOpacity={0.35} />
                  <stop offset="100%" stopColor={ROLE_COLOR[b.role].fill} stopOpacity={0.95} />
                </radialGradient>
              ))}
              {placed.map((b) => (
                <filter key={`glow-${b.id}`} id={`glow-${b.id}`} x="-50%" y="-50%" width="200%" height="200%">
                  <feGaussianBlur stdDeviation={hoveredId === b.id ? 10 : 5} result="blur" />
                  <feMerge>
                    <feMergeNode in="blur" />
                    <feMergeNode in="SourceGraphic" />
                  </feMerge>
                </filter>
              ))}
            </defs>

            {/* Rotate connection lines */}
            {rotateLines.map((line, i) => {
              const mx = (line.src.x + line.tgt.x) / 2;
              const my = (line.src.y + line.tgt.y) / 2 - 40;
              return (
                <path
                  key={i}
                  d={`M ${line.src.x} ${line.src.y} Q ${mx} ${my} ${line.tgt.x} ${line.tgt.y}`}
                  stroke="#a855f7"
                  strokeOpacity={0.25}
                  strokeWidth={1.5}
                  strokeDasharray="4 4"
                  fill="none"
                />
              );
            })}

            {/* Bubbles */}
            {placed.map((b, i) => {
              const isHovered = hoveredId === b.id;
              const c = ROLE_COLOR[b.role];
              const delay = mounted ? 0 : i * 20;
              return (
                <g
                  key={b.id}
                  transform={`translate(${b.x}, ${b.y})`}
                  style={{
                    cursor: "pointer",
                    transition: `transform 0.5s cubic-bezier(.22,.68,0,1.2) ${delay}ms`,
                    opacity: mounted ? 1 : 0,
                  }}
                  onMouseEnter={() => setHoveredId(b.id)}
                >
                  {/* Glow ring on hover */}
                  {isHovered && (
                    <circle
                      r={b.r + 8}
                      fill="none"
                      stroke={c.stroke}
                      strokeWidth={2}
                      strokeOpacity={0.5}
                      className="animate-ping"
                      style={{ animationDuration: "1.4s" }}
                    />
                  )}

                  {/* Main circle */}
                  <circle
                    r={b.r}
                    fill={`url(#grad-${b.id})`}
                    stroke={c.stroke}
                    strokeWidth={isHovered ? 2.5 : 1.5}
                    strokeOpacity={isHovered ? 1 : 0.7}
                    filter={isHovered ? `url(#glow-${b.id})` : undefined}
                    style={{
                      transition: "r 0.4s, stroke-width 0.15s",
                    }}
                  />

                  {/* Token % arc fill (inner pie slice suggestion) */}
                  {b.tokenPct > 0 && b.tokenPct < 100 && (
                    <circle
                      r={b.r * 0.55}
                      fill={c.stroke}
                      fillOpacity={0.15}
                      stroke="none"
                    />
                  )}

                  {/* Label */}
                  {b.r >= 22 && (
                    <>
                      <text
                        textAnchor="middle"
                        dominantBaseline="middle"
                        dy={b.r > 36 ? -8 : 0}
                        fill={c.text}
                        fontSize={b.r > 40 ? 11 : b.r > 28 ? 9 : 8}
                        fontWeight="700"
                        style={{ pointerEvents: "none" }}
                      >
                        {b.label.length > 10 ? b.label.slice(0, 9) + "…" : b.label}
                      </text>
                      {b.r > 36 && (
                        <text
                          textAnchor="middle"
                          dominantBaseline="middle"
                          dy={8}
                          fill={c.text}
                          fillOpacity={0.7}
                          fontSize={8}
                          style={{ pointerEvents: "none" }}
                        >
                          {b.sol > 0 ? `${b.sol.toFixed(2)} SOL` : "—"}
                        </text>
                      )}
                      {b.tokenPct > 0.5 && b.r > 32 && (
                        <text
                          textAnchor="middle"
                          dominantBaseline="middle"
                          dy={22}
                          fill={c.text}
                          fillOpacity={0.5}
                          fontSize={7.5}
                          style={{ pointerEvents: "none" }}
                        >
                          {b.tokenPct.toFixed(1)}%
                        </text>
                      )}
                    </>
                  )}
                </g>
              );
            })}
          </svg>

          {/* Tooltip */}
          {hoveredBubble && (
            <Tooltip
              bubble={hoveredBubble}
              x={mousePos.x}
              y={mousePos.y}
              containerW={dims.w}
            />
          )}
        </div>

        {/* Side panel — top holders */}
        <div className="w-44 shrink-0 border-l border-white/5 flex flex-col bg-black/20 overflow-y-auto">
          <div className="px-3 py-2 text-[10px] font-bold uppercase tracking-widest text-white/30 border-b border-white/5">
            Top Holders
          </div>
          {topHolders.map((b, rank) => (
            <div
              key={b.id}
              className="flex items-center gap-2 px-3 py-2 hover:bg-white/5 cursor-pointer transition-colors border-b border-white/5"
              onMouseEnter={() => setHoveredId(b.id)}
              onMouseLeave={() => setHoveredId(null)}
            >
              <div className="text-[10px] text-white/20 w-4 font-mono">{rank + 1}</div>
              <div
                className="size-2 rounded-full shrink-0"
                style={{ background: ROLE_COLOR[b.role].stroke }}
              />
              <div className="flex-1 min-w-0">
                <div className="text-[11px] font-semibold text-white/80 truncate">{b.label}</div>
                <div className="text-[10px] text-white/30">{b.tokenPct.toFixed(1)}%</div>
              </div>
              {/* Mini bar */}
              <div className="w-10 h-1 rounded-full bg-white/5 overflow-hidden shrink-0">
                <div
                  className="h-full rounded-full transition-all duration-500"
                  style={{
                    width: `${Math.min(100, b.tokenPct * (100 / (topHolders[0]?.tokenPct || 1)))}%`,
                    background: ROLE_COLOR[b.role].stroke,
                  }}
                />
              </div>
            </div>
          ))}
          {topHolders.length === 0 && (
            <div className="flex-1 flex items-center justify-center text-[11px] text-white/20 p-4 text-center">
              Add wallets and buy edges to see token distribution
            </div>
          )}
        </div>
      </div>

      {/* Legend */}
      <Legend />
    </div>
  );
}
